import React, { useContext, useState, useEffect, useReducer, useRef, useCallback } from "react";
import { isSameDay, parseISO, format } from "date-fns";
import clsx from "clsx";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import { blue, green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
  Dialog,
  DialogContent,
  Tooltip,
} from "@material-ui/core";

import {
  AccessTime,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  Facebook,
  Instagram,
  Translate,
  KeyboardArrowDown,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";
import YouTubePreview from "../ModalYoutubeCors";

import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import SelectMessageCheckbox from "./SelectMessageCheckbox";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";
import AudioModal from "../AudioModal";
import AdMetaPreview from "../AdMetaPreview";
// import PdfModal from "../PdfModal";
import LinkPreview from "../LinkPreview";

import { useParams, useHistory } from 'react-router-dom';
import { getBackendUrl } from "../../config";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    // Evita overflow horizontal no mobile
    overflowX: 'hidden',
    // Remover restrição que causava barra horizontal
    // minWidth: 300,
    minHeight: 200,
  },
  loadingCenter: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 2,
  },
  fadeHidden: {
    opacity: 0,
    transition: 'opacity 180ms ease',
    pointerEvents: 'none',
  },
  fadeShown: {
    opacity: 1,
    transition: 'opacity 180ms ease',
  },

  currentTick: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "95%",
    backgroundColor: "transparent",
    margin: "10px",
    borderRadius: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    '&:before': {
      content: '""',
      flex: 1,
      display: 'block',
      height: 'calc(var(--b) + var(--s)/(2*tan(var(--a)/2)))',
      margin: '0 10px',
      background: theme.mode === 'light' ? '#A9A9A9' : '#444',
      '--a': '90deg',
      '--s': '10px',
      '--b': '3px',
      '--_g': 'var(--s) repeat-x conic-gradient(from calc(var(--a)/-2) at bottom, #0000, #000 1deg calc(var(--a) - 1deg), #0000 var(--a))',
      mask: '50% calc(-1*var(--b))/var(--_g) exclude, 50%/var(--_g)',
      WebkitMask: '50% calc(-1*var(--b))/var(--_g) exclude, 50%/var(--_g)',
    },
    '&:after': {
      content: '""',
      flex: 1,
      display: 'block',
      height: 'calc(var(--b) + var(--s)/(2*tan(var(--a)/2)))',
      margin: '0 10px',
      background: theme.mode === 'light' ? '#A9A9A9' : '#444',
      '--a': '90deg',
      '--s': '10px',
      '--b': '3px',
      '--_g': 'var(--s) repeat-x conic-gradient(from calc(var(--a)/-2) at bottom, #0000, #000 1deg calc(var(--a) - 1deg), #0000 var(--a))',
      mask: '50% calc(-1*var(--b))/var(--_g) exclude, 50%/var(--_g)',
      WebkitMask: '50% calc(-1*var(--b))/var(--_g) exclude, 50%/var(--_g)',
    },
  },

  currentTicktText: {
    display: "inline-block",
    fontWeight: 'bold',
    padding: "0 8px",
    alignSelf: "center",
    marginLeft: "0px",
    backgroundColor: "transparent",
  },

  currentTickContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    lineHeight: 1.1,
  },

  currentTickSubText: {
    fontSize: 11,
    color: "#000",
    marginTop: 2,
  },

  messagesList: {
    backgroundImage: theme.mode === 'light' ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`,
    backgroundColor: theme.mode === 'light' ? "transparent" : "#0b0b0d",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "auto",
    overflowX: "hidden", // CRÍTICO: Evita overflow horizontal
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    ...theme.scrollbarStyles,
    // Força contenção dentro do viewport
    maxWidth: "100%",
    boxSizing: "border-box",
    [theme.breakpoints.down('sm')]: {
      // Mais espaço inferior para não ficar encoberto pelo composer
      padding: '8px 12px 20px 12px',
      overflowX: 'hidden',
      overscrollBehavior: 'contain',
    }
  },
  dragElement: {
    background: 'rgba(255, 255, 255, 0.8)',
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: 999999,
    textAlign: "center",
    fontSize: "3em",
    border: "5px dashed #333",
    color: '#333',
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  circleLoading: {
    color: blue[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 150,
    maxWidth: 350, // Mesmo tamanho que messageRight
    height: "auto",
    display: "block",
    position: "relative",
    wordWrap: "break-word",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.mode === 'light' ? "#ffffff" : "#202c33",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000",
    [theme.breakpoints.down('sm')]: {
      marginRight: 10,
      maxWidth: 280,
    },
  },

  // Balão maior apenas para áudios recebidos
  messageLeftAudio: {
    // largura responsiva: cresce até ~760px, respeitando viewport e sidebars
    width: "min(760px, calc(100vw - 220px))",
    maxWidth: 400,
    minWidth: 120,
  },

  // Balão maior para áudios enviados
  messageRightAudio: {
    // espelha o comportamento do lado esquerdo
    width: "min(760px, calc(100vw - 220px)) !important",
    maxWidth: "400px !important",
    minWidth: "120px !important",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.mode === 'light' ? "#f0f0f0" : "#1d282f",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#388aff",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 350, // Mesmo tamanho que messageLeft
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: theme.mode === 'light' ? "#dcf8c6" : "#005c4b",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000",
    [theme.breakpoints.down('sm')]: {
      marginLeft: 10,
      maxWidth: 280,
    },
  },

  messageRightPrivate: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: "#F0E68C",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.mode === 'light' ? "#cfe9ba" : "#025144",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageHighlighted: {
    animation: "$quotedMessagePulse 1.6s ease",
  },
  "@keyframes quotedMessagePulse": {
    "0%": { boxShadow: "0 0 0 0 rgba(33, 150, 243, .75)" },
    "45%": { boxShadow: "0 0 0 6px rgba(33, 150, 243, .28)" },
    "100%": { boxShadow: "0 1px 1px rgba(0,0,0,.18)" },
  },
  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  sentFromPhoneLabel: {
    display: "flex",
    color: "#888",
    fontSize: "0.7rem",
    fontStyle: "italic",
    marginBottom: 2,
    alignItems: "center",
    gap: 4,
    justifyContent: "flex-end",
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },
  textContentItemEdited: {
    paddingRight: 126,
    paddingBottom: 10,
  },

  // Versão compacta do balão quando não há texto exibido (ex.: PDF sem legenda)
  textContentItemCompact: {
    padding: "3px 10px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: "256px !important",
    height: "256px !important",
    marginBottom: 12,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    display: "block",
    [theme.breakpoints.down('sm')]: {
      width: "220px !important",
      height: "220px !important",
    },
  },

  fileFrame: {
    width: 256,
    height: 256,
    maxWidth: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    borderRadius: 8,
    backgroundColor: theme.mode === 'light' ? '#f0f0f0' : '#2a2f32',
    overflow: 'hidden',
    textAlign: 'center',
    padding: 12,
  },
  fileName: {
    fontSize: 13,
    fontWeight: 500,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginTop: 6,
    marginBottom: 8,
  },

  // Classe específica para stickers/GIFs - tamanho pequeno fixo
  stickerMedia: {
    objectFit: "cover",
    maxWidth: "120px !important",
    maxHeight: "120px !important",
    width: "auto !important",
    height: "auto !important",
    marginBottom: 12,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    display: "block",
    [theme.breakpoints.down('sm')]: {
      maxWidth: "100px !important",
      maxHeight: "100px !important",
    },
  },

  mediaWrapper: {
    position: "relative",
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
  },

  hdBadge: {
    position: "absolute",
    top: 6,
    left: 6,
    backgroundColor: "rgba(0,0,0,0.6)",
    color: "#fff",
    fontWeight: 700,
    fontSize: 10,
    padding: "2px 4px",
    borderRadius: 3,
    lineHeight: 1,
    zIndex: 2,
    border: theme.mode === 'light' ? "1px solid rgba(255,255,255,0.8)" : "1px solid rgba(255,255,255,0.5)",
    letterSpacing: 0.5,
    userSelect: "none",
    pointerEvents: "none",
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 8,
    color: "#999",
    display: "inline-flex",
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  editedLabel: {
    fontSize: 10,
    fontStyle: "italic",
    marginRight: 4,
    opacity: 0.85,
  },

  audioDuration: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    left: 80,
    color: "#999",
  },

  forwardMessage: {
    fontSize: 12,
    fontStyle: "italic",
    position: "absolute",
    top: 0,
    left: 5,
    color: "#999",
    display: "flex",
    alignItems: "center"
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: blue[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackPlayedIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },
  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
    color: theme.mode === "light" ? theme.palette.light : theme.palette.dark,
    width: "100%",
  },

  messageCenter: {
    marginTop: 5,
    alignItems: "center",
    verticalAlign: "center",
    alignContent: "center",
    backgroundColor: "#E1F5FEEB",
    fontSize: "12px",
    minWidth: 100,
    maxWidth: 270,
    color: "#272727",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  translationCard: {
    margin: "6px 4px 4px",
    padding: "8px 10px",
    borderRadius: 8,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.mode === "light" ? "#eef6ff" : "#183044",
    fontSize: 13,
    whiteSpace: "pre-wrap",
  },
  translationMeta: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 600,
    opacity: 0.75,
    marginBottom: 4,
  },
  translationActions: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    margin: "3px 4px 5px",
  },
  translationIconSlot: {
    width: 20,
    minWidth: 20,
    display: "flex",
    justifyContent: "center",
    alignSelf: "flex-start",
    marginTop: 2,
    marginRight: -3,
  },
  translationIconButton: {
    width: 22,
    height: 22,
    padding: 3,
    zIndex: 5,
    color: theme.palette.primary.main,
    backgroundColor: theme.mode === "light" ? "#ffffff" : "#202c33",
    border: `1px solid ${theme.mode === "light" ? "#d8e7f5" : "#38515f"}`,
    boxShadow: "0 1px 3px rgba(0,0,0,.18)",
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "#eef6ff" : "#29404c",
    },
  },
  scrollToBottomButton: {
    position: "absolute",
    zIndex: 9,
    right: 18,
    bottom: 78,
    width: 42,
    height: 42,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === "light" ? "#d7d7d7" : "#3c4a50"}`,
    boxShadow: "0 2px 10px rgba(0,0,0,.24)",
    "&:hover": {
      color: "#ffffff",
      backgroundColor: theme.palette.primary.main,
    },
  },
  scrollToBottomBadge: {
    position: "absolute",
    top: -5,
    right: -5,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    padding: "0 4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    fontWeight: 700,
    color: "#ffffff",
    backgroundColor: "#25d366",
  },
  translationIconCompleted: {
    color: "#ffffff !important",
    backgroundColor: "#25a55f !important",
    borderColor: "#208a50 !important",
    "&:hover": {
      backgroundColor: "#1d8c50 !important",
    },
  },
  unreadTranslateBar: {
    position: "absolute",
    zIndex: 8,
    top: 8,
    left: "50%",
    transform: "translateX(-50%)",
    borderRadius: 18,
    textTransform: "none",
    fontWeight: 600,
    color: `${theme.palette.primary.main} !important`,
    border: `1px solid ${theme.palette.primary.main}`,
    boxShadow: "0 2px 8px rgba(0,0,0,.18)",
    backgroundColor: `${theme.palette.background.paper} !important`,
    "&:hover": {
      color: "#ffffff !important",
      backgroundColor: `${theme.palette.primary.main} !important`,
    },
  },
  deletedMessage: {
    color: '#f55d65'
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {

      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "MERGE_LATEST_MESSAGES") {
    const merged = new Map(state.map(message => [Number(message.id), message]));
    (action.payload || []).forEach(message => {
      const current = merged.get(Number(message.id)) || {};
      merged.set(Number(message.id), {
        ...current,
        ...message,
        translations: message.translations || current.translations || [],
        sentTranslations:
          message.sentTranslations || current.sentTranslations || []
      });
    });

    return Array.from(merged.values()).sort((first, second) => {
      const firstDate = new Date(first.createdAt || 0).getTime();
      const secondDate = new Date(second.createdAt || 0).getTime();
      if (firstDate !== secondDate) return firstDate - secondDate;
      return Number(first.id) - Number(second.id);
    });
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      const current = state[messageIndex];
      state[messageIndex] = {
        ...current,
        ...newMessage,
        translations: newMessage.translations || current.translations || [],
        sentTranslations:
          newMessage.sentTranslations || current.sentTranslations || []
      };
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    console.log("🔄 [REDUCER] UPDATE_MESSAGE", {
      messageId: messageToUpdate.id,
      found: messageIndex !== -1,
      index: messageIndex,
      currentAck: messageIndex !== -1 ? state[messageIndex].ack : null,
      newAck: messageToUpdate.ack,
    });

    if (messageIndex !== -1) {
      const current = state[messageIndex];
      state[messageIndex] = {
        ...current,
        ...messageToUpdate,
        translations: messageToUpdate.translations || current.translations || [],
        sentTranslations:
          messageToUpdate.sentTranslations || current.sentTranslations || []
      };
    } else {
      // Upsert: se não existe ainda (ex.: recebemos UPDATE antes do CREATE), adiciona
      console.log("⚠️ [REDUCER] Mensagem não encontrada, adicionando ao state");
      state.push(messageToUpdate);
    }

    return [...state];
  }

  if (action.type === "SET_TRANSLATION") {
    const translation = action.payload;
    const messageIndex = state.findIndex((m) => String(m.id) === String(translation.messageId));
    if (messageIndex !== -1) {
      const message = state[messageIndex];
      const previous = Array.isArray(message.translations) ? message.translations : [];
      state[messageIndex] = {
        ...message,
        translations: [
          translation,
          ...previous.filter(item => String(item.id) !== String(translation.id))
        ]
      };
    }
    return [...state];
  }

  if (action.type === "SET_DETECTED_LANGUAGE") {
    const detection = action.payload;
    const messageIndex = state.findIndex(
      message => Number(message.id) === Number(detection.messageId)
    );
    if (messageIndex !== -1) {
      state[messageIndex] = {
        ...state[messageIndex],
        detectedLanguage: detection.language,
        detectedLanguageConfidence: detection.confidence,
        languageDetectionSource: detection.source
      };
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "DELETE_MESSAGE") {
    const idToRemove = action.payload;
    const filtered = state.filter((m) => m.id !== idToRemove);
    return [...filtered];
  }
};

const MessagesList = ({
  isGroup,
  onDrop,
  whatsappId,
  queueId,
  channel,
  unreadMessages = 0
}) => {
  const classes = useStyles();
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const history = useHistory();
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const { ticketId } = useParams();

  const currentTicketId = useRef(ticketId);
  const { getAll } = useCompanySettings();
  const [dragActive, setDragActive] = useState(false);

  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState(false);
  const { selectedQueuesMessage } = useContext(QueueSelectedContext);

  const { user, socket } = useContext(AuthContext);
  // Armazena a sala atual (idealmente ticket.uuid). Antes de sabermos o uuid, usa-se ticketId como fallback.
  const currentRoomIdRef = useRef(null);
  const currentTicketDbIdRef = useRef(null);
  const knownMessageIdsRef = useRef(new Set());
  const reconcileInFlightRef = useRef(false);

  const { showSelectMessageCheckbox } = useContext(ForwardMessageContext);

  const companyId = user.companyId;

  const [videoDialog, setVideoDialog] = useState({ open: false, url: null });
  const [pdfDialog, setPdfDialog] = useState({ open: false, url: null });
  const [translationLoading, setTranslationLoading] = useState({});
  const [translationVisible, setTranslationVisible] = useState({});
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [pendingNewMessages, setPendingNewMessages] = useState(0);
  const isNearBottomRef = useRef(true);
  const languageDetectionRequestedRef = useRef(new Set());
  const [translatingUnread, setTranslatingUnread] = useState(false);
  const [unreadBatchDone, setUnreadBatchDone] = useState(false);

  // Helper para decidir qual contato exibir no avatar do áudio
  const backendUrl = getBackendUrl();
  // 🔥 CORES CONSISTENTES por participante (estilo WhatsApp)
const PARTICIPANT_COLORS = ["#5b9bd5", "#ed7d31", "#a5a5a5", "#70ad47", "#ffc000", "#c00000", "#7030a0", "#00b0f0", "#92d050", "#ff69b4", "#ff8c00", "#2e8b57", "#8b008b", "#1e90ff", "#dc143c", "#20b2aa", "#d2691e", "#6a5acd", "#2f4f4f", "#cd853f"];
const getParticipantColor = (name) => {
  if (!name) return "#6bcbef";
  let h = 0;
  const s = String(name);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PARTICIPANT_COLORS[h % PARTICIPANT_COLORS.length];
};

const getAvatarContactForMessage = (msg, fallbackTicketContact = null) => {
    try {
      // Mensagens enviadas por mim: usar avatar do usuário logado
      if (msg?.fromMe) {
        const imageName = user?.profileImage;
        const url = imageName
          ? `${backendUrl}/public/company${companyId}/${imageName}`
          : `${process.env.FRONTEND_URL}/nopicture.png`;
        return { name: user?.name, urlPicture: url };
      }

      // Mensagens recebidas: preferir contact com imagem; caso não tenha, usar ticket.contact
      const c = msg?.contact;
      const hasPic = !!(c?.urlPicture || c?.profilePicUrl || c?.contact?.urlPicture || c?.contact?.profilePicUrl);
      if (hasPic) return c;

      // tenta pegar do próprio msg.ticket ou do fallback informado
      const ticketContact = msg?.ticket?.contact || fallbackTicketContact;
      if (ticketContact) return ticketContact;

      return c;
    } catch (e) {
      return msg?.contact || fallbackTicketContact || null;
    }
  };

  // Helper para extrair nome do arquivo no browser
  const getFileNameFromUrl = (url = "") => {
    try {
      const u = new URL(url, window.location.href);
      return u.pathname.split("/").pop() || "";
    } catch (e) {
      return (url || "").split("/").pop() || "";
    }
  };

  // Força download via fetch+blob para evitar abrir na mesma aba
  const handleDirectDownload = async (url) => {
    try {
      if (!url) return;
      const response = await fetch(url);
      if (!response.ok) return;
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = getFileNameFromUrl(url) || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {}
  };

  // Exibe duração do áudio (mm:ss) usando apenas metadata do arquivo
  const AudioDurationTag = ({ src }) => {
    const audioRef = useRef(null);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
      const a = audioRef.current;
      if (!a) return;
      const onLoaded = () => setDuration(a.duration || 0);
      const onDurationChange = () => setDuration(a.duration || 0);
      a.addEventListener("loadedmetadata", onLoaded);
      a.addEventListener("durationchange", onDurationChange);
      return () => {
        a.removeEventListener("loadedmetadata", onLoaded);
        a.removeEventListener("durationchange", onDurationChange);
      };
    }, []);

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const sourceUrl = isIOS ? (src || "").replace(".ogg", ".mp3") : src;

    const formatTime = (s) => {
      if (!isFinite(s) || s <= 0) return "";
      const m = Math.floor(s / 60);
      const sec = Math.floor(s % 60).toString().padStart(2, "0");
      return `${m}:${sec}`;
    };

    return (
      <>
        {formatTime(duration)}
        <audio ref={audioRef} preload="metadata" style={{ display: "none" }}>
          <source src={sourceUrl} type={isIOS ? "audio/mp3" : "audio/ogg"} />
        </audio>
      </>
    );
  };

  // Componente de vídeo com selo HD
  const VideoWithHdBadge = ({ src, className, isGif }) => {
    const [isHd, setIsHd] = useState(false);
    const videoRef = useRef(null);

    const handleLoadedMetadata = useCallback(() => {
      const v = videoRef.current;
      if (!v) return;
      const w = v.videoWidth || 0;
      const h = v.videoHeight || 0;
      setIsHd(w >= 1280 && h >= 720);
    }, []);

    return (
      <div className={classes.mediaWrapper}>
        {!isGif && isHd && <span className={classes.hdBadge}>HD</span>}
        <video
              className={`${classes.messageMedia} ${className || ''}`}
          src={src}
          controls
          ref={videoRef}
          onLoadedMetadata={handleLoadedMetadata}
        />
      </div>
    );
  };

  useEffect(() => {

    async function fetchData() {

      const settings = await getAll(companyId);

      let settinglgpdDeleteMessage;
      let settingEnableLGPD;

      for (const [key, value] of Object.entries(settings)) {

        if (key === "lgpdDeleteMessage") settinglgpdDeleteMessage = value
        if (key === "enableLGPD") settingEnableLGPD = value
      }
      if (settingEnableLGPD === "enabled" && settinglgpdDeleteMessage === "enabled") {
        setLGPDDeleteMessage(true);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
    currentRoomIdRef.current = null;
    currentTicketDbIdRef.current = null;
    knownMessageIdsRef.current = new Set();
    reconcileInFlightRef.current = false;
    setUiReady(false);
    setTranslationLoading({});
    setTranslationVisible({});
    setHighlightedMessageId(null);
    setShowScrollToBottom(false);
    setPendingNewMessages(0);
    isNearBottomRef.current = true;
    languageDetectionRequestedRef.current = new Set();
    setTranslatingUnread(false);
    setUnreadBatchDone(false);
  }, [ticketId, selectedQueuesMessage]);

  useEffect(() => {
    knownMessageIdsRef.current = new Set(
      (messagesList || []).map(message => Number(message.id))
    );
  }, [messagesList]);

  useEffect(() => {
    const forceRefreshCurrentTicket = (event) => {
      const selectedUuid = String(event?.detail?.uuid || "");
      if (!selectedUuid || selectedUuid !== String(ticketId || "")) return;

      dispatch({ type: "RESET" });
      setUiReady(false);
      setHasMore(false);
      knownMessageIdsRef.current = new Set();
      if (pageNumber !== 1) {
        setPageNumber(1);
      } else {
        setRefreshNonce(previous => previous + 1);
      }
    };

    window.addEventListener("ticket-force-refresh", forceRefreshCurrentTicket);
    return () => window.removeEventListener(
      "ticket-force-refresh",
      forceRefreshCurrentTicket
    );
  }, [ticketId, pageNumber]);

  const [uiReady, setUiReady] = useState(false);
  const composerReadyRef = useRef(false);

  useEffect(() => {
    setLoading(true);
    const fetchMessages = async () => {
        if (!ticketId || ticketId === "undefined") {
          history.push("/tickets");
          return;
        }
        if (isNil(ticketId)) return;
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber, selectedQueues: JSON.stringify(selectedQueuesMessage) },
          });

          if (currentTicketId.current === ticketId) {
            currentTicketDbIdRef.current = data?.ticket?.id || null;
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });

            if (user?.translationEnabled) {
              const missingLanguageIds = (data.messages || [])
                .filter(message =>
                  !message.fromMe &&
                  !message.isDeleted &&
                  !["audio", "ptt"].includes(message.mediaType) &&
                  String(message.body || "").trim() &&
                  !message.detectedLanguage &&
                  !languageDetectionRequestedRef.current.has(Number(message.id))
                )
                .slice(-20)
                .map(message => Number(message.id));

              if (missingLanguageIds.length) {
                missingLanguageIds.forEach(id =>
                  languageDetectionRequestedRef.current.add(id)
                );
                api.post("/translations/detect", {
                  messageIds: missingLanguageIds
                }).then(response => {
                  (response.data?.detections || []).forEach(detection => {
                    dispatch({
                      type: "SET_DETECTED_LANGUAGE",
                      payload: detection
                    });
                    try {
                      window.dispatchEvent(new CustomEvent(
                        "conversation-language-detected",
                        { detail: { language: detection.language } }
                      ));
                    } catch {}
                  });
                }).catch(() => {
                  // A detecção é auxiliar; falhas não bloqueiam o histórico.
                });
              }
            }

            setHasMore(data.hasMore);
            setLoading(false);
            setLoadingMore(false);

            // Descobre o UUID do ticket diretamente do payload (mais confiável)
            const ticketUuid = data?.ticket?.uuid || null;
            // Fallback: tenta pegar da primeira mensagem caso necessário
            const firstMsg = data?.messages?.[0];
            const firstMsgUuid = firstMsg?.ticket?.uuid || null;
            const newRoomId = ticketUuid || firstMsgUuid || null;
            if (newRoomId) {
              try {
                const prevRoom = currentRoomIdRef.current;
                if (prevRoom && prevRoom !== newRoomId) {
                  if (typeof socket.leaveRoom === "function") {
                    socket.leaveRoom(prevRoom, (err) => {
                      if (err) console.log("[MessagesList] leave prev room ack error", err);
                      else console.log("[MessagesList] left prev room", { room: prevRoom });
                    });
                  } else {
                    socket.emit("joinChatBoxLeave", prevRoom, (err) => {
                      if (err) console.log("[MessagesList] leave prev room ack error", err);
                      else console.log("[MessagesList] left prev room", { room: prevRoom });
                    });
                  }
                }
                if (prevRoom !== newRoomId) {
                  currentRoomIdRef.current = newRoomId;
                  if (typeof socket.joinRoom === "function") {
                    socket.joinRoom(newRoomId, (err) => {
                      if (err) console.log("[MessagesList] join after fetch ack error", err);
                      else {
                        console.log("[MessagesList] joined room by uuid after fetch", { room: newRoomId });
                        if (typeof socket.checkRoom === "function") {
                          socket.checkRoom(newRoomId, (res) => console.log("[MessagesList] checkRoom after fetch join", res));
                        }
                      }
                    });
                  } else {
                    socket.emit("joinChatBox", newRoomId, (err) => {
                      if (err) console.log("[MessagesList] join after fetch ack error", err);
                      else {
                        console.log("[MessagesList] joined room by uuid after fetch", { room: newRoomId });
                        if (typeof socket.checkRoom === "function") {
                          socket.checkRoom(newRoomId, (res) => console.log("[MessagesList] checkRoom after fetch join", res));
                        }
                      }
                    });
                  }
                }
              } catch {}
            }
          }

          if (pageNumber === 1) {
            // aguarda composer e layout
            const doReady = () => {
              setUiReady(true);
              requestAnimationFrame(() => {
                requestAnimationFrame(() => scrollToBottom("auto"));
              });
              try { window.dispatchEvent(new Event('messages-ready')); } catch {}
            };
            if (composerReadyRef.current) {
              setTimeout(doReady, 30);
            } else {
              // fallback: garante readiness mesmo sem evento
              setTimeout(doReady, 180);
            }
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
          setLoadingMore(false);
        }
    };

    fetchMessages();
  }, [pageNumber, ticketId, selectedQueuesMessage, user?.translationEnabled, refreshNonce]);

  // Reconciliação leve: o socket continua sendo o caminho principal, mas uma
  // consulta curta corrige eventos perdidos ao trocar rapidamente de ticket,
  // voltar à aba ou sofrer uma reconexão de rede. A API permanece isolada pelo
  // companyId do JWT e pelo ticket atual.
  useEffect(() => {
    if (!ticketId || ticketId === "undefined") return undefined;
    let active = true;

    const reconcileLatestMessages = async () => {
      if (!active || reconcileInFlightRef.current) return;
      reconcileInFlightRef.current = true;
      try {
        const { data } = await api.get(`/messages/${ticketId}`, {
          params: {
            pageNumber: 1,
            selectedQueues: JSON.stringify(selectedQueuesMessage)
          }
        });

        if (!active || currentTicketId.current !== ticketId) return;
        const latest = data?.messages || [];
        const unseen = latest.filter(
          message => !knownMessageIdsRef.current.has(Number(message.id))
        );

        if (unseen.length) {
          unseen.forEach(message =>
            knownMessageIdsRef.current.add(Number(message.id))
          );
          dispatch({ type: "MERGE_LATEST_MESSAGES", payload: latest });

          if (isNearBottomRef.current) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => scrollToBottom("smooth"));
            });
          } else {
            setShowScrollToBottom(true);
            setPendingNewMessages(previous => previous + unseen.length);
          }
        }
      } catch {
        // A reconciliação nunca bloqueia o chat; o próximo ciclo tenta de novo.
      } finally {
        reconcileInFlightRef.current = false;
      }
    };

    const initialCheck = setTimeout(reconcileLatestMessages, 1500);
    const interval = setInterval(reconcileLatestMessages, 12000);
    const handleFocus = () => reconcileLatestMessages();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        reconcileLatestMessages();
      }
    };
    const handleOnline = () => reconcileLatestMessages();

    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      active = false;
      clearTimeout(initialCheck);
      clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [ticketId, selectedQueuesMessage]);

  // Garante que, quando o composer sinalizar que está pronto, a lista role ao final
  useEffect(() => {
    const onComposerReady = () => {
      composerReadyRef.current = true;
      setTimeout(() => {
        setUiReady(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => scrollToBottom("auto"));
        });
        try { window.dispatchEvent(new Event('messages-ready')); } catch {}
      }, 60);
    };
    window.addEventListener('composer-ready', onComposerReady);
    return () => window.removeEventListener('composer-ready', onComposerReady);
  }, []);

  useEffect(() => {
    const handleLocalMessageSent = (event) => {
      const message = event?.detail?.message;
      if (!message) return;
      dispatch({ type: "ADD_MESSAGE", payload: message });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToBottom("smooth"));
      });
    };

    window.addEventListener("local-message-sent", handleLocalMessageSent);
    return () => window.removeEventListener(
      "local-message-sent",
      handleLocalMessageSent
    );
  }, []);

  useEffect(() => {
    if (!ticketId || ticketId === "undefined") {
      return;
    }

    // Aguarda socket e user.companyId disponíveis
    if (!socket || typeof socket.on !== "function") {
      return;
    }
    if (!user || !user.companyId) {
      return;
    }

    const companyId = user.companyId;

    const connectEventMessagesList = () => {
      try {
        // Prioriza entrar pela sala UUID se já conhecida
        const normalizedId = (ticketId ?? "").toString().trim();
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        const candidateFromUrl = uuidRegex.test(normalizedId) ? normalizedId : "";
        const roomToJoin = (currentRoomIdRef.current || candidateFromUrl || "").toString().trim();
        if (!roomToJoin || roomToJoin === "undefined") {
          console.debug("[MessagesList] skip joinChatBox - invalid ticketId", { ticketId });
          return;
        }
        console.log("[MessagesList] socket connect - joinChatBox", { room: roomToJoin, hasJoinRoom: typeof socket.joinRoom === "function", connected: !!socket.connected });
        if (typeof socket.joinRoom === "function") {
          socket.joinRoom(roomToJoin, (err) => {
            if (err) console.log("[MessagesList] joinChatBox ack error", err);
            else {
              console.log("[MessagesList] joinChatBox ok", { room: roomToJoin });
              if (typeof socket.checkRoom === "function") {
                socket.checkRoom(roomToJoin, (res) => console.log("[MessagesList] checkRoom after connect join", res));
              }
            }
          });
        } else {
          socket.emit("joinChatBox", roomToJoin, (err) => {
            if (err) console.log("[MessagesList] joinChatBox ack error", err);
            else {
              console.log("[MessagesList] joinChatBox ok", { room: roomToJoin });
              if (typeof socket.checkRoom === "function") {
                socket.checkRoom(roomToJoin, (res) => console.log("[MessagesList] checkRoom after connect join", res));
              }
            }
          });
        }
      } catch (e) {
        console.debug("[MessagesList] error emitting joinChatBox", e);
      }
    };

    const onAppMessageMessagesList = (data) => {
      try {
        const evtUuid = data?.message?.ticket?.uuid || data?.ticket?.uuid;
        const hasUuid = Boolean(evtUuid);

        console.log("📨 [FRONTEND] Evento socket.io recebido", {
          action: data?.action,
          messageId: data?.message?.id,
          ack: data?.message?.ack,
          evtUuid,
          hasUuid,
          currentRoom: currentRoomIdRef.current,
        });

        const eventTicketId =
          data?.message?.ticketId ||
          data?.message?.ticket?.id ||
          data?.ticket?.id ||
          null;
        const shouldHandle = hasUuid
          ? evtUuid === currentRoomIdRef.current
          : Boolean(
              eventTicketId &&
              currentTicketDbIdRef.current &&
              Number(eventTicketId) === Number(currentTicketDbIdRef.current)
            );

        if (!shouldHandle) {
          console.log("⚠️ [FRONTEND] Evento ignorado - UUID não corresponde");
          return;
        }

        if (data.action === "create") {
          console.log("➕ [FRONTEND] Chamando dispatch ADD_MESSAGE");
          console.log("📋 [FRONTEND] message.contact:", data.message?.contact);
          console.log("📋 [FRONTEND] message.contactId:", data.message?.contactId);
          dispatch({ type: "ADD_MESSAGE", payload: data.message });
          if (isNearBottomRef.current || data.message?.fromMe) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => scrollToBottom("smooth"));
            });
          } else {
            setShowScrollToBottom(true);
            setPendingNewMessages(previous => previous + 1);
          }
        }

        if (data.action === "update") {
          console.log("🔄 [FRONTEND] Chamando dispatch UPDATE_MESSAGE", {
            messageId: data.message.id,
            ack: data.message.ack
          });
          dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
          if (!data.message?.fromMe && data.message?.detectedLanguage) {
            try {
              window.dispatchEvent(new CustomEvent(
                "conversation-language-detected",
                { detail: { language: data.message.detectedLanguage } }
              ));
            } catch {}
          }
        }

        if (data.action == "delete") {
          console.log("🗑️ [FRONTEND] Chamando dispatch DELETE_MESSAGE");
          dispatch({ type: "DELETE_MESSAGE", payload: data.messageId });
        }
      } catch (e) {
        console.error("[MessagesList] error handling appMessage", e, data);
      }
    };

    socket.on("connect", connectEventMessagesList);
    socket.on(`company-${companyId}-appMessage`, onAppMessageMessagesList);

    // Se já estiver conectado, entra na sala imediatamente
    try {
      if (socket && socket.connected) {
        connectEventMessagesList();
      }
    } catch {}

    // Logs auxiliares de conexão
    socket.on("disconnect", (reason) => console.debug("[MessagesList] disconnect", reason));
    socket.on("reconnect", (attempt) => console.debug("[MessagesList] reconnect", attempt));
    socket.on("reconnect_attempt", (attempt) => console.debug("[MessagesList] reconnect_attempt", attempt));
    socket.on("connect_error", (err) => console.debug("[MessagesList] connect_error", err?.message || err));

    return () => {
      try {
        const roomToLeave = (currentRoomIdRef.current || (ticketId ?? "")).toString().trim();
        if (!roomToLeave || roomToLeave === "undefined") {
          console.debug("[MessagesList] skip leave room - invalid ticketId", { ticketId });
        } else {
          console.debug("[MessagesList] cleanup - leave room", { room: roomToLeave });
          if (typeof socket.leaveRoom === "function") {
            socket.leaveRoom(roomToLeave, (err) => {
              if (err) console.debug("[MessagesList] joinChatBoxLeave ack error", err);
              else console.debug("[MessagesList] joinChatBoxLeave ok", { room: roomToLeave });
            });
          } else {
            socket.emit("joinChatBoxLeave", roomToLeave, (err) => {
              if (err) console.debug("[MessagesList] joinChatBoxLeave ack error", err);
              else console.debug("[MessagesList] joinChatBoxLeave ok", { room: roomToLeave });
            });
          }
        }
      } catch {}

      socket.off("connect", connectEventMessagesList);
      socket.off(`company-${companyId}-appMessage`, onAppMessageMessagesList);
      socket.off("disconnect");
      socket.off("reconnect");
      socket.off("reconnect_attempt");
      socket.off("connect_error");
    };
  }, [ticketId, socket, user?.companyId]);

  const loadMore = () => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = (behavior = "auto") => {
    setShowScrollToBottom(false);
    setPendingNewMessages(0);
    isNearBottomRef.current = true;
    setTimeout(() => {
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({
          behavior,
          block: "end"
        });
      }
    }, 80);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const nearBottom = distanceFromBottom < 120;

    isNearBottomRef.current = nearBottom;
    setShowScrollToBottom(!nearBottom);
    if (nearBottom) setPendingNewMessages(0);

    if (!hasMore) return;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) return;

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const hanldeReplyMessage = (e, message) => {
    setAnchorEl(null);
    setReplyingMessage(message);
  };

  const focusMessage = (messageId) => {
    setTimeout(() => {
      const element = document.getElementById(`message-${messageId}`);
      if (!element) return;
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedMessageId(Number(messageId));
      setTimeout(() => setHighlightedMessageId(null), 1800);
    }, 80);
  };

  const scrollToQuotedMessage = async (quotedMessageId) => {
    if (!quotedMessageId) return;

    const existing = document.getElementById(`message-${quotedMessageId}`);
    if (existing) {
      focusMessage(quotedMessageId);
      return;
    }

    try {
      const { data } = await api.get(`/messages/show/${quotedMessageId}`);
      if (data) {
        dispatch({ type: "LOAD_MESSAGES", payload: [data] });
        focusMessage(quotedMessageId);
      }
    } catch (error) {
      toast.info("A mensagem original não está mais disponível.");
    }
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    } else if (message.mediaType === "contactMessage") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} queueId={message?.ticket?.queueId} whatsappId={message?.ticket?.whatsappId} />
    } else if (message.mediaType === "adMetaPreview") {
      let [image, sourceUrl, title, body] = message.body.split('|');
      let messageUser = "Olá! Tenho interesse e queria mais informações, por favor.";
      return <AdMetaPreview image={image} sourceUrl={sourceUrl} title={title} body={body} messageUser={messageUser} />;
    }

    if (message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      return (
        <AudioModal url={message.mediaUrl} contact={getAvatarContactForMessage(message, message?.ticket?.contact)} fromMe={message.fromMe} />
      );
    } else if (message.mediaType === "video") {
      return (
        <div onClick={(e) => { e.preventDefault(); setVideoDialog({ open: true, url: message.mediaUrl }); }} style={{ display: 'inline-block', cursor: 'pointer' }}>
          <VideoWithHdBadge
            className={classes.messageMedia}
            src={message.mediaUrl}
            isGif={/\.gif(\?.*)?$/i.test(message.mediaUrl || "")}
          />
        </div>
      );
    } else if (message.mediaType === "application" && /\.pdf($|\?)/i.test(message.mediaUrl)) {
      return (
        <div className={classes.fileFrame} onClick={(e) => { e.preventDefault(); setPdfDialog({ open: true, url: message.mediaUrl }); }} style={{ cursor: 'pointer' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>PDF</div>
            <div className={classes.fileName}>{getFileNameFromUrl(message.mediaUrl) || 'arquivo.pdf'}</div>
            <Button
              startIcon={<GetApp />}
              variant="outlined"
              href={message.mediaUrl}
              onClick={(e) => { e.preventDefault(); handleDirectDownload(message.mediaUrl); }}
            >
              Baixar
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <>
          <div className={classes.fileFrame}>
            <div>
              <GetApp color="primary" />
              <div className={classes.fileName}>{getFileNameFromUrl(message.mediaUrl) || 'arquivo'}</div>
              <Button
                startIcon={<GetApp />}
                variant="outlined"
                href={message.mediaUrl}
                onClick={(e) => { e.preventDefault(); handleDirectDownload(message.mediaUrl); }}
              >
                Download
              </Button>
            </div>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    } else if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    } else if (message.ack === 2 || message.ack === 3) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    } else if (message.ack === 4 || message.ack === 5) {
      return <DoneAll fontSize="small" className={message.mediaType === "audio" ? classes.ackPlayedIcon : classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    const today = format(new Date(), "dd/MM/yyyy");
    const messageDay = parseISO(message.createdAt);
    const previousMessageDay = index > 0
      ? parseISO(messagesList[index - 1].createdAt)
      : null;

    if (index === 0 || !isSameDay(messageDay, previousMessageDay)) {
      const messageDate = format(messageDay, "dd/MM/yyyy");
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {today === messageDate ? "HOJE" : messageDate}
          </div>
        </span>
      );
    }

    return null;
  };


  const renderTicketsSeparator = (message, index) => {
    let lastTicket = messagesList[index - 1]?.ticketId;
    let currentTicket = message.ticketId;

    if (lastTicket !== currentTicket && lastTicket !== undefined) {
      if (message?.ticket?.queue) {
        return (
          <span
            className={classes.currentTick}
            key={`timestamp-${message.id}a`}
          >
            <div className={classes.currentTickContent}>
              <div
                className={classes.currentTicktText}
                style={{ color: message?.ticket?.queue?.color || "#666" }}
              >
                #{i18n.t("ticketsList.called")} {message?.ticketId} - {message?.ticket?.queue?.name}
              </div>
              <div className={classes.currentTickSubText}>
                {format(parseISO(message?.ticket?.createdAt || message.createdAt), "dd/MM/yy - HH'h'mm")}
              </div>
            </div>

          </span>
        );
      } else {
        return (
          <span
            className={classes.currentTick}
            key={`timestamp-${message.id}b`}
          >
            <div className={classes.currentTickContent}>
              <div
                className={classes.currentTicktText}
                style={{ color: "#666" }}
              >
                #{i18n.t("ticketsList.called")} {message.ticketId} - {i18n.t("ticketsList.noQueue")}
              </div>
              <div className={classes.currentTickSubText}>
                {format(parseISO(message?.ticket?.createdAt || message.createdAt), "dd/MM/yyyy HH:mm")}
              </div>
            </div>

          </span>
        );
      }
    }

  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;
      if (messageUser !== previousMessageUser) {
        return (

          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {

    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
        onClick={(event) => {
          event.stopPropagation();
          scrollToQuotedMessage(message.quotedMsg?.id || message.quotedMsgId);
        }}
        title="Ir para a mensagem original"
        style={{ cursor: "pointer" }}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}

          {message.quotedMsg.mediaType === "audio"
            && (
              <div className={classes.downloadMedia}>
                <AudioModal url={message.quotedMsg.mediaUrl} contact={getAvatarContactForMessage(message.quotedMsg, message?.ticket?.contact)} fromMe={message.quotedMsg?.fromMe} />
              </div>
            )
          }
          {message.quotedMsg.mediaType === "video"
            && (
              <VideoWithHdBadge
                className={classes.messageMedia}
                src={message.quotedMsg.mediaUrl}
                isGif={/\.gif(\?.*)?$/i.test(message.quotedMsg.mediaUrl || "")}
              />
            )
          }
          {message.quotedMsg.mediaType === "contactMessage"
            && (
              "Contato"
            )
          }
          {message.quotedMsg.mediaType === "application" && /\.pdf($|\?)/i.test(message.quotedMsg.mediaUrl)
            && (
              <div className={classes.fileFrame} onClick={(e) => { e.preventDefault(); setPdfDialog({ open: true, url: message.quotedMsg.mediaUrl }); }} style={{ cursor: 'pointer' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>PDF</div>
                  <div className={classes.fileName}>{getFileNameFromUrl(message.quotedMsg.mediaUrl) || 'arquivo.pdf'}</div>
                  <Button
                    startIcon={<GetApp />}
                    variant="outlined"
                    href={message.quotedMsg.mediaUrl}
                    onClick={(e) => { e.preventDefault(); handleDirectDownload(message.quotedMsg.mediaUrl); }}
                  >
                    Baixar
                  </Button>
                </div>
              </div>
            )
          }
          {message.quotedMsg.mediaType === "application" && !/\.pdf($|\?)/i.test(message.quotedMsg.mediaUrl)
            && (
              <div className={classes.downloadMedia}>
                <Button
                  startIcon={<GetApp />}
                  variant="outlined"
                  href={message.quotedMsg.mediaUrl}
                  onClick={(e) => { e.preventDefault(); handleDirectDownload(message.quotedMsg.mediaUrl); }}
                >
                  Download
                </Button>
              </div>
            )
          }

          {message.quotedMsg.mediaType === "image"
            && (
              <ModalImageCors imageUrl={message.quotedMsg.mediaUrl} />)
            || message.quotedMsg?.body}

          {!message.quotedMsg.mediaType === "image" && message.quotedMsg?.body}


        </div>
      </div>
    );
  };

  const handleDrag = event => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "dragenter" || event.type === "dragover") {
      setDragActive(true);
    } else if (event.type === "dragleave") {
      setDragActive(false);
    }
  }

  const isYouTubeLink = (url) => {
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    return youtubeRegex.test(url);
  };

  const urlRegex = /https?:\/\/[^\s]+/gi;
  const extractFirstUrl = (text = "") => {
    try {
      const m = text.match(urlRegex);
      return m && m.length
        ? m[0].replace(/[),.;!?]+$/, "")
        : "";
    } catch { return ""; }
  };

  const thumbnailToDataUrl = (thumbnail) => {
    try {
      const data = thumbnail?.data || thumbnail;
      if (!data || !Array.isArray(data)) return null;
      let binary = "";
      for (let index = 0; index < data.length; index += 1) {
        binary += String.fromCharCode(data[index]);
      }
      return `data:image/jpeg;base64,${window.btoa(binary)}`;
    } catch {
      return null;
    }
  };

  const getLinkPreviewMetadata = (message) => {
    const fallbackUrl = extractFirstUrl(message?.body || "");
    if (!fallbackUrl) return null;

    try {
      const parsed = typeof message.dataJson === "string"
        ? JSON.parse(message.dataJson)
        : (message.dataJson || {});
      const raw = parsed.raw || parsed;
      const extended = raw?.message?.extendedTextMessage || {};
      const external = extended?.contextInfo?.externalAdReply || {};
      const image = thumbnailToDataUrl(
        extended.jpegThumbnail || external.thumbnail
      );

      return {
        url: extended.canonicalUrl || extended.matchedText || external.sourceUrl || fallbackUrl,
        title: extended.title || external.title || "",
        description: extended.description || external.body || "",
        image
      };
    } catch {
      return { url: fallbackUrl, title: "", description: "", image: null };
    }
  };

  const handleDrop = event => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      if (onDrop) {
        onDrop(event.dataTransfer.files);
      }
    }
  }
  const xmlRegex = /<([^>]+)>/g;
  const boldRegex = /\*(.*?)\*/g;

  const formatXml = (xmlString) => {
    if (boldRegex.test(xmlString)) {
      xmlString = xmlString.replace(boldRegex, "**$1**");
    }
    return xmlString;
  };

    const isCampaignMessage = (message) => {
    // Verifica APENAS se tem o flag _isCampaignResponse
    if (!message.fromMe || !message.dataJson) return false;
    try {
      const data = typeof message.dataJson === 'string'
        ? JSON.parse(message.dataJson)
        : message.dataJson;
      // APENAS verifica o flag, não tempo
      return data._isCampaignResponse === true;
    } catch {
      return false;
    }
  };

  const isSentFromPhone = (message) => {
    // Verifica se foi enviado do celular (SEM ser campanha)
    if (!message.fromMe || !message.dataJson) return false;
    try {
      const data = typeof message.dataJson === 'string'
        ? JSON.parse(message.dataJson)
        : message.dataJson;
      // _sentFromPhone = true AND _isCampaignResponse = false ou undefined
      return data._sentFromPhone === true && data._isCampaignResponse !== true;
    } catch {
      return false;
    }
  };

  const languageName = (code) => ({
    de: "Alemão", en: "Inglês", es: "Espanhol", it: "Italiano",
    nl: "Holandês", pl: "Polonês", pt: "Português", sv: "Sueco"
  }[String(code || "").split("-")[0]] || String(code || "").toUpperCase());

  const isTranslatableText = (message) => Boolean(
    message &&
    !message.fromMe &&
    !message.isDeleted &&
    String(message.body || "").trim() &&
    message.mediaType !== "reactionMessage" &&
    message.mediaType !== "audio" &&
    message.mediaType !== "call_log"
  );

  const normalizeLanguageCode = (code) =>
    String(code || "").trim().toLowerCase().replace("_", "-").split("-")[0];

  const isTranslatableAudio = (message) => Boolean(
    message &&
    !message.fromMe &&
    !message.isDeleted &&
    ["audio", "ptt"].includes(message.mediaType)
  );

  const isTranslatableImage = (message) => Boolean(
    message &&
    !message.fromMe &&
    !message.isDeleted &&
    message.mediaType === "image" &&
    message.mediaUrl
  );

  const getMessageTranslation = (message) => {
    const target = normalizeLanguageCode(user?.translationReadLanguage || "pt");
    return (message?.translations || []).find(
      item => normalizeLanguageCode(item.targetLanguage) === target
    ) || null;
  };

  const getSentTranslation = (message) =>
    (message?.sentTranslations || []).find(
      item => item.direction === "outbound" && item.status === "sent"
    ) || null;

  const shouldOfferTranslation = (message) => {
    if (!user?.translationEnabled) return false;

    // Antes da primeira transcrição o botão fica visível. Depois, se o áudio
    // estiver no mesmo idioma do agente, a ação continua disponível somente no
    // menu da mensagem, evitando poluir a lateral do chat.
    if (isTranslatableAudio(message) || isTranslatableImage(message)) {
      const existing = getMessageTranslation(message);
      if (!existing) return true;
      return normalizeLanguageCode(existing.sourceLanguage) !==
        normalizeLanguageCode(existing.targetLanguage);
    }
    if (!isTranslatableText(message)) return false;

    const source = normalizeLanguageCode(message.detectedLanguage);
    const target = normalizeLanguageCode(user?.translationReadLanguage || "pt");
    // Aguarda a detecção assíncrona. Isso também evita oferecer tradução para
    // mensagens no mesmo idioma configurado para leitura do agente.
    return Boolean(source && target && source !== target);
  };

  const toggleTranslationVisibility = (messageId, visible) => {
    setTranslationVisible(previous => ({
      ...previous,
      [messageId]: visible === undefined ? !previous[messageId] : visible
    }));
  };

  const handleTranslateMessage = async (message) => {
    if (!message || translationLoading[message.id]) return;

    const existingTranslation = getMessageTranslation(message);
    const canUseTranslationAction =
      isTranslatableAudio(message) ||
      isTranslatableImage(message) ||
      shouldOfferTranslation(message);

    // No menu, áudios no mesmo idioma continuam podendo abrir a transcrição
    // já salva, mesmo quando o ícone lateral foi ocultado para reduzir ruído.
    if (existingTranslation && canUseTranslationAction) {
      toggleTranslationVisibility(message.id);
      return;
    }

    if (!canUseTranslationAction) return;

    setTranslationLoading(previous => ({ ...previous, [message.id]: true }));
    try {
      const endpoint = isTranslatableAudio(message)
        ? `/translations/messages/${message.id}/audio`
        : isTranslatableImage(message)
          ? `/translations/messages/${message.id}/image`
          : `/translations/messages/${message.id}`;
      const { data } = await api.post(endpoint);
      dispatch({ type: "SET_TRANSLATION", payload: data.translation });
      toggleTranslationVisibility(message.id, true);
    } catch (error) {
      toastError(error);
    } finally {
      setTranslationLoading(previous => ({ ...previous, [message.id]: false }));
    }
  };

  const handleTranslateUnread = async () => {
    if (translatingUnread) return;
    const candidates = messagesList
      .filter(message =>
        !isTranslatableAudio(message) &&
        !isTranslatableImage(message) &&
        shouldOfferTranslation(message) &&
        !getMessageTranslation(message)
      );
    const requestedCount = Math.max(0, Math.min(Number(unreadMessages || candidates.length), 10));
    const selected = candidates.slice(-requestedCount).map(message => String(message.id));
    if (!selected.length) {
      setUnreadBatchDone(true);
      toast.info("Não há mensagens não lidas pendentes de tradução.");
      return;
    }

    setTranslatingUnread(true);
    try {
      const { data } = await api.post("/translations/unread", { messageIds: selected });
      (data.translations || []).forEach(translation => {
        dispatch({ type: "SET_TRANSLATION", payload: translation });
        toggleTranslationVisibility(translation.messageId, true);
      });
      setUnreadBatchDone(true);
      if (data.errors?.length) {
        toast.warn(`${data.errors.length} mensagem(ns) não possuem rota direta disponível.`);
      }
    } catch (error) {
      toastError(error);
    } finally {
      setTranslatingUnread(false);
    }
  };

  const renderMessages = () => {
    if (!messagesList || messagesList.length === 0) {
      return <div>Diga olá para seu novo contato!</div>;
    }
    const view = messagesList.map((message, index) => { if (message.mediaType === "reactionMessage") return null;
      if (message.mediaType === "call_log") {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderTicketsSeparator(message, index)}
            {renderMessageDivider(message, index)}
            <div className={classes.messageCenter}>
              {(() => { 
                console.log("🔍 [RENDER] Renderizando mensagem:", { 
                  messageId: message.id, 
                  isGroup: isGroup, 
                  hasContact: !!message.contact,
                  contactName: message.contact?.name,
                  fromMe: message.fromMe
                });
                if (isGroup && message.contact?.name) {
                  return (<span className={classes.messageContactName}>{message.contact.name}</span>);
                }
                return null;
              })()}
              <div>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17"><path fill="#df3333" d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path></svg>
                <span>{i18n.t("ticketsList.missedCall")} {format(parseISO(message.createdAt), "HH:mm")}</span>
              </div>
            </div>
          </React.Fragment>
        );
      }

      const isLeft = !message.fromMe;
      const bubbleClass = clsx(
        isLeft ? classes.messageLeft : (message.isPrivate ? classes.messageRightPrivate : classes.messageRight),
        { [isLeft ? classes.messageLeftAudio : classes.messageRightAudio]: message.mediaType === "audio" }
      );
      // 🔥 ESPACO p/ reacoes penduradas (evita cobrir a hora / a proxima msg)
      let hasReactions = false;
      try {
        hasReactions = buildReactionGroups(messagesList, message).length > 0;
      } catch (e) { hasReactions = false; }
      const bubbleStyle = {
        position: "relative",
        ...(hasReactions ? { marginBottom: "32px", paddingBottom: "16px" } : {})
      };
      // 🔥 BLOCO (estilo WhatsApp): avatar so na ULTIMA do bloco; nome so na PRIMEIRA
      const isGroupMsg = isGroup && !message.fromMe;
      const prevMsg = index > 0 ? messagesList[index - 1] : null;
      const nextMsg = index < messagesList.length - 1 ? messagesList[index + 1] : null;
      
      const sameSender = (a, b) => {
        if (!a || !b) return false;
        if (a.fromMe !== b.fromMe) return false;
        const idA = a.participant || a.contactId || (a.fromMe ? "me" : null);
        const idB = b.participant || b.contactId || (b.fromMe ? "me" : null);
        return !!idA && !!idB && idA === idB;
      };
      
      const prevReal = prevMsg && prevMsg.mediaType === "reactionMessage" ? null : prevMsg;
      const nextReal = nextMsg && nextMsg.mediaType === "reactionMessage" ? null : nextMsg;
      
      const isFirstOfBlock = isGroupMsg && !(prevReal && sameSender(prevReal, message));
      const isLastOfBlock = isGroupMsg && !(nextReal && sameSender(nextReal, message));
      
      const avatarContact = getAvatarContactForMessage(message, message?.ticket?.contact);
      const showAvatar = isGroupMsg && isLastOfBlock;
      
      const avatarImg = (() => {
        const pic = avatarContact?.urlPicture || avatarContact?.profilePicUrl;
        const name = String(avatarContact?.name || "?").trim();
        const initial = (name.charAt(0) || "?").toUpperCase();
        const base = {
          width: 32, height: 32, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          flex: "none", marginTop: 4, overflow: "hidden"
        };
        if (pic) {
          return <img src={pic} alt="" style={{ ...base, objectFit: "cover" }} />;
        }
        // 🔥 fallback: inicial do nome num circulo colorido (estilo WhatsApp)
        const colors = ["#5b9bd5", "#ed7d31", "#a5a5a5", "#70ad47", "#ffc000", "#c00000", "#7030a0", "#00b0f0", "#92d050", "#ff69b4"];
        let h = 0;
        for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
        return (
          <div style={{ ...base, backgroundColor: colors[h % colors.length], color: "#fff", fontSize: 14, fontWeight: 600, textTransform: "uppercase", userSelect: "none" }}>
            {initial}
          </div>
        );
      })();
      


      return (
        <React.Fragment key={message.id}>
          {renderDailyTimestamps(message, index)}
          {renderTicketsSeparator(message, index)}
          {renderMessageDivider(message, index)}
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, maxWidth: "100%", justifyContent: isLeft ? "flex-start" : "flex-end" }}>
          {showAvatar ? avatarImg : (isGroupMsg ? <div style={{ width: 32, flex: "none" }} /> : null)}
          {isLeft && user?.translationEnabled && (
            <div className={classes.translationIconSlot}>
              {shouldOfferTranslation(message) && (
                <Tooltip title={getMessageTranslation(message)
                  ? "Mostrar/ocultar tradução"
                  : isTranslatableAudio(message)
                    ? "Transcrever e traduzir áudio"
                    : isTranslatableImage(message)
                      ? "Extrair e traduzir texto da imagem"
                      : "Traduzir mensagem"}>
                  <span>
                    <IconButton
                      size="small"
                      className={clsx(classes.translationIconButton, {
                        [classes.translationIconCompleted]:
                          Boolean(getMessageTranslation(message))
                      })}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleTranslateMessage(message);
                      }}
                      disabled={Boolean(translationLoading[message.id])}
                      aria-label="traduzir mensagem"
                    >
                      {translationLoading[message.id] ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Translate style={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </div>
          )}
          <div
            id={`message-${message.id}`}
            className={clsx(bubbleClass, {
              [classes.messageHighlighted]:
                Number(highlightedMessageId) === Number(message.id)
            })}
            style={{ ...bubbleStyle, flex: "0 1 auto" }}

            title={message.queueId && message.queue?.name}
            onDoubleClick={(e) => hanldeReplyMessage(e, message)}
          >
            {/* 🔥 FIX: Mostrar nome do participante em grupos */}
            {isFirstOfBlock && message.contact?.name && (
              <span className={classes.messageContactName} style={{ color: getParticipantColor(message.contact?.name || message.participant || "") }}>{message.contact.name}</span>
            )}
            {showSelectMessageCheckbox && (<SelectMessageCheckbox message={message} />)}
            <IconButton
              variant="contained"
              size="small"
              id="messageActionsButton"
              disabled={message.isDeleted}
              className={classes.messageActionsButton}
              onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
            >
              <ExpandMore />
            </IconButton>

                        {isCampaignMessage(message) && (
              <span className={classes.sentFromPhoneLabel}>
                📱 Campanha em massa
              </span>
            )}
            
            {isSentFromPhone(message) && !isCampaignMessage(message) && (
              <span className={classes.sentFromPhoneLabel}>
                📱 Enviado pelo celular
              </span>
            )}

            {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage" || message.mediaType === "template" || message.mediaType === "adMetaPreview") && checkMessageMedia(message)}

            <div className={clsx(
              classes.textContentItem,
              {
                [classes.textContentItemDeleted]: message.isDeleted,
                [classes.textContentItemEdited]: message.isEdited && !message.isDeleted,
                [classes.textContentItemCompact]: (
                  // PDFs: compacta quando body == nome do arquivo
                  (message.mediaType === "application" && /\.pdf($|\?)/i.test(message.mediaUrl || "") &&
                   (getFileNameFromUrl(message.mediaUrl) || "").trim() === (message.body || "").trim())
                  ||
                  // Imagens/Vídeos: compacta quando não há legenda ou quando body == nome do arquivo
                  ((message.mediaType === "image" || message.mediaType === "video") && (
                    ((message.body || "").trim() === "") ||
                    ((getFileNameFromUrl(message.mediaUrl) || "").trim() === (message.body || "").trim())
                  ))
                )
              }
            )}>
              {message.quotedMsg && renderQuotedMessage(message)}
              {/* 🔥 REACOES AGRUPADAS (estilo WhatsApp) */}
              <ReactionPills message={message} allMessages={messagesList} />
              {/* 🔥 REACAO: mostra o emoji no balao (estilo WhatsApp) */}
              {message.mediaType === "reactionMessage" && (
                <span
                  style={{
                    fontSize: "1.6em",
                    lineHeight: 1.2,
                    display: "inline-block",
                    padding: "2px 0",
                    verticalAlign: "middle"
                  }}
                >
                  {message.body && message.body !== "reaction" ? message.body : ""}
                </span>
              )}
              {message.mediaType !== "adMetaPreview" && (
                (
                  ((message.mediaType === "image" || message.mediaType === "video") && (getFileNameFromUrl(message.mediaUrl) || "").trim() !== (message.body || "").trim()) ||
                  (
                    message.mediaType !== "audio" &&
                    message.mediaType != "reactionMessage" &&
                    message.mediaType != "locationMessage" &&
                    message.mediaType !== "contactMessage" &&
                    !(message.mediaType === "application" && /\.pdf($|\?)/i.test(message.mediaUrl || "") && (getFileNameFromUrl(message.mediaUrl) || "").trim() === (message.body || "").trim())
                  )
                ) && (xmlRegex.test(message.body) ? <span>{formatXml(message.body)}</span> : <MarkdownWrapper>{(lgpdDeleteMessage && message.isDeleted) ? "🚫 _Mensagem apagada_ " : message.body}</MarkdownWrapper>)
              )}

              {message.mediaType !== "audio" &&
                message.mediaType !== "reactionMessage" &&
                getLinkPreviewMetadata(message) && (() => {
                  const preview = getLinkPreviewMetadata(message);
                  return (
                    <div style={{ marginTop: 6, marginBottom: 4 }}>
                      <LinkPreview
                        url={preview.url}
                        title={preview.title}
                        description={preview.description}
                        image={preview.image}
                      />
                    </div>
                  );
                })()}

              {(shouldOfferTranslation(message) || isTranslatableAudio(message) || isTranslatableImage(message)) &&
                getMessageTranslation(message) &&
                translationVisible[message.id] && (
                  <div className={classes.translationCard}>
                    <div className={classes.translationMeta}>
                      <Translate fontSize="small" />
                      {languageName(getMessageTranslation(message).sourceLanguage)} → {languageName(getMessageTranslation(message).targetLanguage)}
                      <span>• tradução interna</span>
                    </div>
                    {(isTranslatableAudio(message) || isTranslatableImage(message)) ? (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3 }}>
                          {isTranslatableAudio(message)
                            ? "Transcrição original"
                            : "Texto extraído da imagem"}
                        </div>
                        <MarkdownWrapper>
                          {getMessageTranslation(message).originalText}
                        </MarkdownWrapper>
                        {normalizeLanguageCode(getMessageTranslation(message).sourceLanguage) !==
                          normalizeLanguageCode(getMessageTranslation(message).targetLanguage) && (
                          <>
                            <Divider style={{ margin: "7px 0" }} />
                            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3 }}>
                              Tradução
                            </div>
                            <MarkdownWrapper>
                              {getMessageTranslation(message).translatedText}
                            </MarkdownWrapper>
                          </>
                        )}
                      </>
                    ) : (
                      <MarkdownWrapper>
                        {getMessageTranslation(message).translatedText}
                      </MarkdownWrapper>
                    )}
                  </div>
                )}

              {!isLeft &&
                getSentTranslation(message) &&
                translationVisible[message.id] && (
                  <div className={classes.translationCard}>
                    <div className={classes.translationMeta}>
                      <Translate fontSize="small" />
                      Original em {languageName(getSentTranslation(message).sourceLanguage)}
                      <span>• salvo internamente</span>
                    </div>
                    <MarkdownWrapper>{getSentTranslation(message).originalText}</MarkdownWrapper>
                  </div>
                )}

              {message.mediaType === "audio" && (
                <span className={classes.audioDuration}>
                  <AudioDurationTag src={message.mediaUrl} />
                </span>
              )}

              <span className={classes.timestamp}>
                {message.isEdited && (
                  <span className={classes.editedLabel}>Editada</span>
                )}
                <span>{format(parseISO(message.createdAt), "HH:mm")}</span>
                {!isLeft && renderMessageAck(message)}
              </span>
            </div>
          </div>
          {!isLeft && user?.translationEnabled && (
            <div className={classes.translationIconSlot}>
              {getSentTranslation(message) && (
                <Tooltip title="Mostrar/ocultar texto original salvo">
                  <IconButton
                    size="small"
                    className={classes.translationIconButton}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleTranslationVisibility(message.id);
                    }}
                    aria-label="mostrar texto original"
                  >
                    <Translate style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          )}
                  </div>
        </React.Fragment>
      );
    });
    return view;
  };

  return (
    <div className={classes.messagesListWrapper} onDragEnter={handleDrag}>
      {dragActive && <div className={classes.dragElement} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>Solte o arquivo aqui</div>}

      {user?.translationEnabled && !unreadBatchDone && messagesList.some(message => !isTranslatableAudio(message) && !isTranslatableImage(message) && shouldOfferTranslation(message) && !getMessageTranslation(message)) && (
        <Button
          className={classes.unreadTranslateBar}
          size="small"
          variant="contained"
          color="primary"
          startIcon={translatingUnread ? <CircularProgress size={14} color="inherit" /> : <Translate fontSize="small" />}
          onClick={handleTranslateUnread}
          disabled={translatingUnread}
        >
          Traduzir mensagens não lidas
        </Button>
      )}

      {showScrollToBottom && (
        <Tooltip title="Ir para a mensagem mais recente">
          <IconButton
            className={classes.scrollToBottomButton}
            onClick={() => scrollToBottom("smooth")}
            aria-label="ir para a última mensagem"
          >
            <KeyboardArrowDown />
            {pendingNewMessages > 0 && (
              <span className={classes.scrollToBottomBadge}>
                {pendingNewMessages > 99 ? "99+" : pendingNewMessages}
              </span>
            )}
          </IconButton>
        </Tooltip>
      )}

      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        isGroup={isGroup}
        whatsappId={whatsappId}
        queueId={queueId}
        onTranslateMessage={handleTranslateMessage}
        canTranslateMessage={Boolean(
          user?.translationEnabled &&
          selectedMessage &&
          !selectedMessage.fromMe &&
          (
            isTranslatableText(selectedMessage) ||
            isTranslatableAudio(selectedMessage) ||
            isTranslatableImage(selectedMessage)
          )
        )}
        translationAvailable={Boolean(getMessageTranslation(selectedMessage))}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {uiReady && (messagesList.length > 0 ?
          renderMessages()
          : [])}
        <div
          ref={lastMessageRef}
          aria-hidden="true"
          style={{
            height: 64,
            minHeight: 64,
            flexShrink: 0,
            pointerEvents: "none"
          }}
        />
      </div>
      {!uiReady && (
        <div className={classes.messagesList} style={{ position: 'absolute', inset: 0, padding: 20, pointerEvents: 'none' }}>
          <div className={classes.skelBubbleLeft}>
            <div className={classes.skelLine} style={{ width: 220 }} />
            <div className={classes.skelLine} style={{ width: 180 }} />
            <div className={classes.skelLine} style={{ width: 140 }} />
          </div>
          <div className={classes.skelBubbleRight}>
            <div className={classes.skelLine} style={{ width: 200 }} />
            <div className={classes.skelLine} style={{ width: 160 }} />
          </div>
          <div className={classes.skelBubbleLeft}>
            <div className={classes.skelLine} style={{ width: 260 }} />
          </div>
        </div>
      )}

      {(channel !== "whatsapp" && channel !== undefined) && (
        <div
          style={{
            width: "100%",
            display: "flex",
            padding: "10px",
            alignItems: "center",
            backgroundColor: "#E1F3FB",
          }}
        >
          {channel === "facebook" ? (
            <Facebook />
          ) : (
            <Instagram />
          )}

          <span>
            Você tem 24h para responder após receber uma mensagem, de acordo
            com as políticas do Facebook.
          </span>
        </div>
      )}
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}

      <Dialog open={videoDialog.open} onClose={() => setVideoDialog({ open: false, url: null })} maxWidth="md">
        <DialogContent style={{ padding: 0 }}>
          {videoDialog.url && (
            <video src={videoDialog.url} controls style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={pdfDialog.open} onClose={() => setPdfDialog({ open: false, url: null })} maxWidth="lg" fullWidth>
        <DialogContent style={{ padding: 0 }}>
          {pdfDialog.url && (
            <iframe title="PDF" src={pdfDialog.url} style={{ width: '100%', height: '85vh', border: 'none' }} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesList;


// ============================================================
// 🔥 REACOES AGRUPADAS (estilo WhatsApp) - adicionado por patch
// ============================================================
function buildReactionGroups(allMessages, targetMessage) {
  if (!allMessages || !targetMessage) return [];
  const targetWid = targetMessage.wid || null;
  const targetDbId = targetMessage.id != null ? Number(targetMessage.id) : null;
  if (!targetWid && !targetDbId) return [];

  // Dedupe por REAGENTE (a ultima reacao vence - igual WhatsApp)
  const seen = new Map();
  for (const m of allMessages) {
    if (!m || m.mediaType !== "reactionMessage") continue;
    const qId = m.quotedMsgId;
    const qNum = qId != null ? Number(qId) : null;
    const match = (targetDbId && qNum === targetDbId) || (targetWid && qId === targetWid);
    if (!match) continue;
    const emoji = String(m.body || "").trim();
    if (!emoji || emoji === "reaction") continue;

    let raw = {};
    try {
      raw = typeof m.dataJson === "string" ? JSON.parse(m.dataJson) : (m.dataJson || {});
    } catch (e) { raw = {}; }

    const lid = String(raw?.key?.participant || m.participant || "").replace(/:\d+@/, "@");
    const pn = String(raw?.key?.participantPn || "");
    const number = String(pn || lid || "").split("@")[0].replace(/:\d+$/, "");
    const name = raw?.pushName || number || "?";
    // ignora reacoes sem identificacao (lixo de versoes antigas)
    if (!lid && !pn && !number) continue;
    const reactorKey = lid || pn || m.wid || m.id;

    // overwrite = a reacao MAIS RECENTE do reagente vence
    seen.set(reactorKey, { emoji, name, number });
  }

  const byEmoji = new Map();
  seen.forEach((v) => {
    if (!byEmoji.has(v.emoji)) {
      byEmoji.set(v.emoji, { emoji: v.emoji, count: 0, reactors: [] });
    }
    const g = byEmoji.get(v.emoji);
    g.count += 1;
    g.reactors.push({ name: v.name, number: v.number });
  });

  return Array.from(byEmoji.values()).sort((a, b) => b.count - a.count);
}


// 🔥 REACOES - textos traduziveis (pt/en/es)
const REACTION_LANG = {
  pt: { you: "Você reagiu com", reacted: "reagiu com", to: "a:", all: "Todas", reactions: "Reações", reactionsWith: "Reações com", see: "Ver reações" },
  en: { you: "You reacted with", reacted: "reacted with", to: "to:", all: "All", reactions: "Reactions", reactionsWith: "Reactions with", see: "See reactions" },
  es: { you: "Reaccionaste con", reacted: "reaccionó con", to: "a:", all: "Todas", reactions: "Reacciones", reactionsWith: "Reacciones con", see: "Ver reacciones" }
};
function getReactionLang() {
  let l = "pt";
  try {
    if (typeof i18n !== "undefined" && i18n && i18n.language) l = i18n.language;
    else if (typeof navigator !== "undefined" && navigator.language) l = navigator.language;
  } catch (e) {}
  const base = String(l).split("-")[0].toLowerCase();
  return REACTION_LANG[base] ? base : "pt";
}

function ReactionPills(props) {
  const [open, setOpen] = React.useState(false);
  const [filter, setFilter] = React.useState(null);

  const groups = buildReactionGroups(props.allMessages || [], props.message);
  if (!groups.length) return null;

  const total = groups.reduce((s, g) => s + g.count, 0);
  const shown = filter ? groups.filter((g) => g.emoji === filter) : groups;
  const rt = REACTION_LANG[getReactionLang()];

  const st = {
    pillRow: {
      position: "absolute", bottom: "-16px", left: "10px", zIndex: 4,
      display: "flex", flexWrap: "wrap", gap: "4px",
      background: "#fff", borderRadius: "12px", padding: "2px 6px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.22)", cursor: "pointer"
    },
    pill: {
      background: "rgba(0,0,0,0.06)", borderRadius: "12px", padding: "2px 8px",
      fontSize: "0.85rem", display: "inline-flex", alignItems: "center", gap: "4px", lineHeight: 1.4
    },
    overlay: {
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.45)", zIndex: 99999,
      display: "flex", alignItems: "center", justifyContent: "center"
    },
    modal: {
      background: "#fff", borderRadius: "12px", width: "min(90vw, 380px)",
      maxHeight: "70vh", display: "flex", flexDirection: "column",
      boxShadow: "0 8px 30px rgba(0,0,0,0.25)", overflow: "hidden"
    },
    header: {
      padding: "12px 16px", borderBottom: "1px solid #eee",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      fontWeight: 600, fontSize: "1rem"
    },
    close: { cursor: "pointer", color: "#777", fontSize: "1.1rem" },
    tabs: { display: "flex", flexWrap: "wrap", gap: "6px", padding: "12px 16px 4px" },
    list: { overflowY: "auto", padding: "8px 16px 16px", flex: 1 },
    row: {
      display: "flex", alignItems: "center", gap: "10px",
      padding: "7px 0", borderBottom: "1px solid #f5f5f5"
    },
    rowEmoji: { fontSize: "1.4rem", width: "28px", textAlign: "center" },
    rowName: { fontSize: "0.95rem", fontWeight: 600, color: "#222" },
    rowNumber: { fontSize: "0.8rem", color: "#888" }
  };
  const tabStyle = (active) => ({
    cursor: "pointer", padding: "5px 10px", borderRadius: "14px", fontSize: "0.9rem",
    background: active ? "#e7f3ff" : "#f1f1f1",
    border: active ? "1px solid #3390ec" : "1px solid transparent",
    color: active ? "#1a6fd0" : "#333"
  });

  return (
    <React.Fragment>
      <div
        style={st.pillRow}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setFilter(null); setOpen(true); }}
        title="Ver reações"
      >
        {groups.map((g) => (
          <span key={g.emoji} style={st.pill}>
            {g.emoji} <b>{g.count}</b>
          </span>
        ))}
      </div>

      {open && (
        <div style={st.overlay} onClick={() => setOpen(false)}>
          <div style={st.modal} onClick={(e) => e.stopPropagation()}>
            <div style={st.header}>
              <span>{filter ? rt.reactionsWith + " " + filter : rt.reactions}</span>
              <span style={st.close} onClick={() => setOpen(false)}>✕</span>
            </div>
            <div style={st.tabs}>
              <span style={tabStyle(filter === null)} onClick={() => setFilter(null)}>
                {rt.all} <b>{total}</b>
              </span>
              {groups.map((g) => (
                <span key={g.emoji} style={tabStyle(filter === g.emoji)} onClick={() => setFilter(g.emoji)}>
                  {g.emoji} <b>{g.count}</b>
                </span>
              ))}
            </div>
            <div style={st.list}>
              {shown.map((g) =>
                g.reactors.map((r) => (
                  <div key={g.emoji + "|" + r.number + "|" + r.name} style={st.row}>
                    <span style={st.rowEmoji}>{g.emoji}</span>
                    <div>
                      <div style={st.rowName}>{r.name}</div>
                      {r.number ? <div style={st.rowNumber}>{r.number}</div> : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}
